import React, { ReactElement, useCallback, useEffect, useMemo, useState } from 'react';
import { keepPreviousData } from '@tanstack/react-query';
import { MenuProps } from 'antd';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { queryKeysClient } from 'common/clients/query-key.client';
import { queryClient } from 'common/clients/query.client';
import { Table } from 'common/components/antd/Table/Table';
import { ENV_COUNTERPARTY_GUID } from 'common/constants/env.constants';
import { DEFAULT_PAGE, DEFAULT_PAGE_SIZE } from 'common/constants/table.constants';
import { EQueryKeys } from 'common/enums/query-kyes.enum';
import { EAssetAction, EAssetColumnKeys } from 'domains/assets/enums/assets.enum';
import { useGetAssetsListQuery } from 'domains/assets/queries/assets.query';
import { TCounterpartyAssetsResData } from 'domains/counterparties/types/counterparty.types';
import { ROUTES_MAPPING } from 'navigation/constants/route.constants';
import {
  SDangerActionItem,
} from './assets.page.styles';
import { EAssetsPageGetParams, EAssetsPageGetParamsActionTypes, EModalType } from './enums/assets.enum';
import CreateAssetModal from './modals/create-asset.modal';
import { getColumns } from './table/asset-table.columns';
import { TAssetsModalData, TItemClickEventHandler } from './types/assets.types';

type TPaginationConfig = {
  page: number;
  pageSize: number;
};

const AssetsPage = (): ReactElement => {
  const navigate = useNavigate();
  const [paginationData, setPaginationData] = useState<TPaginationConfig>({
    page: DEFAULT_PAGE,
    pageSize: DEFAULT_PAGE_SIZE,
  });
  const { t } = useTranslation();
  const [modalData, setModalData] = useState<TAssetsModalData | {}>({});

  const { data: assetsData } = useGetAssetsListQuery(
    ENV_COUNTERPARTY_GUID,
    {
      limit: paginationData.pageSize,
      offset: (paginationData.page - 1) * paginationData.pageSize,
    },
    {
      refetchOnMount: false,
      placeholderData: keepPreviousData,
    },
  );

  useEffect(() => {
    const searchParams = new URLSearchParams(document.location.search);
    const pageAction = searchParams.get(EAssetsPageGetParams.Action);

    if (pageAction === EAssetsPageGetParamsActionTypes.Create) {
      setModalData({
        type: EModalType.Create,
        rowData: {},
      });
    }
  }, [document.location.search]);

  const actions: MenuProps['items'] = useMemo(
    () => [
      {
        key: EAssetAction.Disable,
        label: t('assets.table.action.block'),
      },
      {
        key: EAssetAction.Delete,
        label: <SDangerActionItem>{t('assets.table.action.delete')}</SDangerActionItem>,
      },
    ],
    [],
  );

  const onItemActionClick = useCallback<TItemClickEventHandler>(() => {
    const fn = ({ key }: { key: string }): void => {
      switch (key) {
        case EAssetAction.Disable: {
          throw new Error('Disabling is not implemented');
        }
        case EAssetAction.Delete: {
          throw new Error('Deletion is not implemented');
        }
        default: {
          throw new Error(`click handler for menu item with key [${key}] is not implemented`);
        }
      }
    };
    return fn;
  }, []);

  const columns = getColumns({
    menuItems: actions,
    onItemActionClick,
  });

  const onChangePaginationHandler = (pageParam: number, pageSizeParam: number): void => {
    setPaginationData({
      page: pageParam,
      pageSize: pageSizeParam,
    });
  };

  const paginationConfig = {
    style: {
      marginRight: 30,
    },
    showQuickJumper: true,
    showSizeChanger: true,
    total: assetsData?.total || 0,
    pageSize: paginationData.pageSize,
    current: paginationData.page,
    onChange: onChangePaginationHandler,
  };

  const onCancelModalHandler = (): void => {
    setModalData({});
    navigate(ROUTES_MAPPING.PROTECTED.ASSETS);
  };

  return (
    <>
      <Table
        pagination={paginationConfig}
        rowKey={EAssetColumnKeys.Issuer}
        dataSource={assetsData?.stellar_assets || []}
        columns={columns}
      />
      <CreateAssetModal isOpen={!!Object.keys(modalData).length} onCancelModalHandler={onCancelModalHandler} />
    </>
  );
};

export const loader = async (): Promise<{ assetsData: TCounterpartyAssetsResData | {} }> => {
  try {
    const assetQuery =
      queryKeysClient[EQueryKeys.AssetQueryKeys].getAssetsList_MultiRole(
        ENV_COUNTERPARTY_GUID,
        {
          offset: (DEFAULT_PAGE - 1) * DEFAULT_PAGE_SIZE,
          limit: DEFAULT_PAGE_SIZE,
        }
      );

    const assetsData: TCounterpartyAssetsResData = queryClient.getQueryData(assetQuery.queryKey)
      ?? (await queryClient.fetchQuery(assetQuery));

    return {
      assetsData,
    };
  } catch (error) {
    console.error(error);

    return {
      assetsData: {},
    };
  }
};

export default AssetsPage;
